import { StripePermissions } from '@/types/Organization';
import {
  Action,
  ConditionalPolicyData,
  EvaluatePolicyArgs,
  OptionalPolicyData,
  OrganizationPolicyData,
  OrganizationResource,
  OrganizationResourceData,
  PolicyAction,
  PolicyActionWithoutResource,
  WorkspacePolicyData,
  WorkspaceResource,
  WorkspaceResourceData,
} from './types';
import {
  organizationPolicies,
  organizationRoles,
  subscriptionRoles,
  workspacePolicies,
  workspaceRoles,
} from './roles';
import { isUserRole } from '@/utils/typeGuards';
import { UserRole } from '@/types/types';
import { ALLOWED_SUBSCRIPTION_STATUSES } from './consts';

export function evaluatePolicy<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
>(
  args: Omit<EvaluatePolicyArgs<L, T>, 'resourceData'> & { resourceData?: never },
): [boolean, string | boolean];

export function evaluatePolicy<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
>(args: EvaluatePolicyArgs<L, T>): [boolean, string | boolean];

export function evaluatePolicy<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  args:
    | EvaluatePolicyArgs<L, T>
    | (Omit<EvaluatePolicyArgs<L, T>, 'resourceData'> & { resourceData?: never }),
): readonly [boolean, string | boolean] {
  const { policyLevel, resource, action, policyData, constraints, resourceData } = args;

  const isRoleAllowingAction = checkRolePermission(
    policyLevel,
    policyData,
    resource,
    action,
  );

  const subscriptionTier =
    policyLevel === 'organization'
      ? (policyData as OrganizationPolicyData).selectedOrganization.subscriptionTier
      : (policyData as WorkspacePolicyData).selectedWorkspace.subscriptionTier || 'basic';

  const subscriptionStatus =
    policyLevel === 'organization'
      ? (policyData as OrganizationPolicyData).selectedOrganization.subscriptionStatus
      : (policyData as WorkspacePolicyData).selectedWorkspace.subscriptionStatus ||
        'basic';

  const isSubscriptionAllowingAction = checkSubscriptionPermission(
    subscriptionTier,
    subscriptionStatus,
    resource as WorkspaceResource | OrganizationResource,
    action,
  );

  const isPolicyAllowingAction = checkPolicy<L, T, R, A>(
    policyLevel,
    resource,
    action,
    constraints,
    policyData,
    resourceData as T[R][A] extends never ? undefined : T[R][A],
  );

  const isRoleAndSubscriptionAllowingAction =
    isRoleAllowingAction && isSubscriptionAllowingAction;

  return [isRoleAndSubscriptionAllowingAction, isPolicyAllowingAction] as const;
}

function checkPolicy<
  L extends 'organization' | 'workspace',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  policyLevel: L,
  resource: keyof T,
  action: Action,
  constraints: StripePermissions,
  policyData: ConditionalPolicyData,
  resourceData: T[R][A] extends never ? undefined : T[R][A],
): boolean | string {
  const policy =
    policyLevel === 'organization'
      ? organizationPolicies[resource as OrganizationResource]
      : workspacePolicies[resource as WorkspaceResource];
  if (policy && typeof policy[action] !== 'undefined') {
    return handlePolicyAction({
      action,
      constraints,
      policy: policy as Partial<{
        [A in keyof T[R]]: PolicyAction<T, R, A, ConditionalPolicyData>;
      }>,
      policyData,
      resourceData,
    });
  }

  // No policy specified, so allow by default
  return true;
}

function checkRolePermission<
  L extends 'organization' | 'workspace',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
>(
  policyLevel: L,
  policyData: OptionalPolicyData,
  resource: keyof T,
  action: Action,
): boolean {
  const roles = policyLevel === 'organization' ? organizationRoles : workspaceRoles;
  if (policyLevel === 'organization' && !isUserRole(policyData.organizationRole))
    return false;
  const userRole =
    policyLevel === 'organization'
      ? policyData.organizationRole
      : policyData.workspaceRole;
  const resourceRole =
    roles[userRole as UserRole][resource as OrganizationResource & WorkspaceResource];

  const isRoleAllowingAction = resourceRole.includes(action);
  return isRoleAllowingAction;
}

function checkSubscriptionPermission(
  subscriptionTier: string,
  subscriptionStatus: string | null,
  resource: WorkspaceResource | OrganizationResource,
  action: Action,
): boolean {
  if (
    subscriptionTier !== 'basic' &&
    !ALLOWED_SUBSCRIPTION_STATUSES.includes(subscriptionStatus ?? '')
  ) {
    return false;
  }

  const roles = subscriptionRoles[subscriptionTier as keyof typeof subscriptionRoles];
  const resourceRole = roles[resource];

  if (!resourceRole) {
    return false;
  }

  return resourceRole.includes(action);
}

function handlePolicyAction<T, R extends keyof T, A extends keyof T[R]>({
  action,
  constraints,
  policy,
  policyData,
  resourceData,
}: {
  action: Action;
  constraints: StripePermissions;
  policy: Partial<{
    [A in keyof T[R]]: PolicyAction<T, R, A, ConditionalPolicyData>;
  }>;
  policyData: ConditionalPolicyData;
  resourceData: T[R][A] extends never ? undefined : T[R][A];
}): boolean | string {
  const policyAction = policy[action as A];

  if (typeof policyAction === 'boolean' || typeof policyAction === 'string') {
    return policyAction;
  } else if (typeof policyAction === 'function') {
    return resourceData
      ? policyAction(policyData, constraints, resourceData)
      : (
          policyAction as Exclude<
            PolicyActionWithoutResource<ConditionalPolicyData>,
            boolean | string
          >
        )(policyData, constraints);
  }

  // No policy specified, so allow by default
  return true;
}
